import { z } from 'zod';

/**
 * SIL-289 — LLM-extracted BOM for a Supermicro "pre-config" (page-sourced,
 * no live v3 configurator) server model. Mirrors
 * `apps/ingestion/src/oem/preconfig-extractor.ts`'s system prompt output
 * shape; validated with `.safeParse` before any DB write, same pattern as
 * `oemSpecPayloadSchema` (dto/oem-spec.ts).
 *
 * `category` is deliberately restricted to the value-bearing spec classes
 * `base-config-composite-mint.ts`'s `SpecAssetClassId` recognises
 * ('cpu'|'gpu'|'memory'|'storage') plus 'server' (the barebone/chassis line
 * item, kept in the BOM audit trail but not a spec-bearing class there).
 *
 * `system_price_usd` is optional/nullable and NOT authoritative — SIL-289
 * follow-up: the LLM was fed `cheerio.load(html).text()`, which drops the
 * price entirely (it lives in an HTML attribute / script JSON, not a text
 * node), so the LLM always returned 0/absent. The price is now extracted
 * deterministically from the raw page HTML
 * (`pipeline/pre-config-composite-mint.ts`'s `extractSystemPriceUsd`) and is
 * the ONLY valid system price source; this field is kept for schema
 * flexibility only and is never read by the mint pipeline.
 */
export const preconfigComponentRowSchema = z.object({
  category: z.enum(['server', 'cpu', 'gpu', 'memory', 'storage']),
  /** Manufacturer part number/SKU if the page shows one; null when the page never surfaces a distinct PN for this line item. */
  part_number: z.string().min(1).nullable().optional(),
  description: z.string().min(1),
  /** ACTUAL configured quantity (e.g. 8 for an 8-GPU build) — never the page's max-capability number. */
  quantity: z.number().int().positive(),
});
export type PreconfigComponentRow = z.infer<typeof preconfigComponentRowSchema>;

export const preconfigSpecPayloadSchema = z.object({
  /** Not authoritative — see module doc. Optional/nullable because the LLM no longer extracts price. */
  system_price_usd: z.number().positive().nullable().optional(),
  components: z.array(preconfigComponentRowSchema).min(1),
});
export type PreconfigSpecPayload = z.infer<typeof preconfigSpecPayloadSchema>;
